import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import Calendar, { CalendarProps } from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import {
  HiOutlineXMark,
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineChevronRight,
  HiOutlineChevronLeft,
  HiOutlineUserPlus
} from 'react-icons/hi2';
import { toast } from 'react-toastify';
import { createUser } from '../../../../../../../../../../crud/users_api/users';
import { RoleOption, PuestoOption } from '../../types';
import './CreateUser.scss';

interface CreateUserProps {
  showCreateModal: boolean;
  setShowCreateModal: (show: boolean) => void;
  setCurrentData: (data: any) => void;
  currentData: any[];
  originalUsers: any[];
  setOriginalUsers: (users: any[]) => void;
  puestos: PuestoOption[];
  roles: RoleOption[];
}

interface FormData {
  username: string;
  last_name: string;
  middle_name: string;
  email: string;
  password: string;
  phone_number: string;
  address: string;
  dob: string;
  rol_id: number | null;
  puesto_id: number | null;
}

const initialFormData: FormData = {
  username: '',
  last_name: '',
  middle_name: '',
  email: '',
  password: '',
  phone_number: '',
  address: '',
  dob: '',
  rol_id: null,
  puesto_id: null
};

const CreateUser: React.FC<CreateUserProps> = ({
  showCreateModal,
  setShowCreateModal,
  setCurrentData,
  currentData,
  setOriginalUsers,
  puestos,
  roles,
}) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showDobCalendar, setShowDobCalendar] = useState(false);

  useEffect(() => {
    if (showCreateModal) {
      setFormData(initialFormData);
      setCurrentStep(1);
      setError(null);
      setShowPassword(false);
      setShowDobCalendar(false);
    }
  }, [showCreateModal]);

  const handleChange = (field: keyof FormData, value: string | number | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  // Convierte el string a Date o null (soporta YYYY-MM-DD)
  const parseDateString = (str: string) => {
    if (!str) return null;
    return new Date(`${str}T00:00:00`);
  };

  // Formatea para mostrar al usuario (DD-MM-YYYY)
  const formatDisplayDate = (date: Date): string => {
    const year  = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day   = date.getDate().toString().padStart(2, '0');
    return `${day}-${month}-${year}`;
  };

  // Formatea para guardar en el estado (YYYY-MM-DD)
  const formatStateDate = (date: Date): string => {
    const year  = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day   = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDobChange: CalendarProps['onChange'] = (value) => {
    const date = Array.isArray(value) ? value[0] : value;
    if (date) {
      const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const formatted = formatStateDate(localDate);
      handleChange('dob', formatted);
    }
    setShowDobCalendar(false);
  };

  const validateStep1 = (): boolean => {
    if (!formData.username.trim()) {
      setError('El nombre es requerido');
      return false;
    }
    if (!formData.last_name.trim()) {
      setError('El apellido es requerido');
      return false;
    }
    if (!formData.email.trim()) {
      setError('El email es requerido');
      return false;
    }
    if (!formData.email.includes('@') || !formData.email.includes('.')) {
      setError('El email no tiene un formato válido');
      return false;
    }
    if (!formData.password) {
      setError('La contraseña es requerida');
      return false;
    }
    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }
    return true;
  };

  const validateStep2 = (): boolean => {
    if (!formData.rol_id) {
      setError('Debe seleccionar un rol');
      return false;
    }
    if (!formData.puesto_id) {
      setError('Debe seleccionar un puesto');
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      setError(null);
      setCurrentStep(2);
    }
  };

  const handlePrevStep = () => {
    setError(null);
    setCurrentStep(1);
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const userContext = localStorage.getItem('user');
      const localId = userContext ? JSON.parse(userContext).local_id : 0;

      const userData = {
        username: formData.username.trim(),
        last_name: formData.last_name.trim(),
        middle_name: formData.middle_name?.trim() || null,
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        phone_number: formData.phone_number?.trim() || '',
        address: formData.address?.trim() || '',
        dob: formData.dob || null,
        local_id: localId,
        rol_id: formData.rol_id,
        puesto_id: formData.puesto_id,
        state: true
      };

      const response: any = await createUser(userData);
      toast.success('Usuario creado exitosamente');
      
      const createdUser = response.data;
      const newUsers = [createdUser, ...currentData];
      setCurrentData(newUsers);
      setOriginalUsers(newUsers);
      setShowCreateModal(false);
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Error al crear el usuario';
      setError(typeof message === 'string' ? message : 'Error al crear el usuario');
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setShowCreateModal(false);
  };

  const selectedRole = roles.find(r => r.rol_id === formData.rol_id);
  const selectedPuesto = puestos.find(p => p.puesto_id === formData.puesto_id);

  return (
    <Modal
      show={showCreateModal}
      onHide={handleClose}
      centered
      backdrop={isSubmitting ? 'static' : true}
      keyboard={!isSubmitting}
      dialogClassName="mp-modal"
    >
      <div className="mp-modal-header">
        <div className="form-modal-title-container">
          <h5 className="form-modal-title">Crear Usuario</h5>
        </div>
        <button className="mp-modal-close" onClick={handleClose} disabled={isSubmitting}>
          <HiOutlineXMark size={18} />
        </button>
      </div>

      <div className="mp-modal-body">
        <div className="mp-step-indicator">
          <div className={`mp-step ${currentStep === 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
            <span className="mp-step-number">1</span>
            <span>Información Personal</span>
          </div>
          <div className="mp-step-divider"></div>
          <div className={`mp-step ${currentStep === 2 ? 'active' : ''}`}>
            <span className="mp-step-number">2</span>
            <span>Rol y Puesto</span>
          </div>
        </div>

        {error && <div className="mp-form-error">{error}</div>}

        {currentStep === 1 && (
          <>
            <div className="mp-form-row">
              <div className="mp-form-group">
                <label className="mp-form-label">Nombre *</label>
                <input className="mp-form-input" type="text" value={formData.username} onChange={(e) => handleChange('username', e.target.value)} placeholder="Juan" disabled={isSubmitting} />
              </div>
              <div className="mp-form-group">
                <label className="mp-form-label">Apellido *</label>
                <input className="mp-form-input" type="text" value={formData.last_name} onChange={(e) => handleChange('last_name', e.target.value)} placeholder="Pérez" disabled={isSubmitting} />
              </div>
            </div>

            <div className="mp-form-group">
              <label className="mp-form-label">Email *</label>
              <input className="mp-form-input" type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="juan.perez@email.com" disabled={isSubmitting} />
            </div>

            <div className="mp-form-group">
              <label className="mp-form-label">Contraseña *</label>
              <div className="mp-form-password">
                <input className="mp-form-input" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => handleChange('password', e.target.value)} placeholder="Mínimo 6 caracteres" disabled={isSubmitting} />
                <button type="button" className="mp-form-password-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                  {showPassword ? <HiOutlineEyeSlash size={18} /> : <HiOutlineEye size={18} />}
                </button>
              </div>
            </div>

            <div className="mp-form-row">
              <div className="mp-form-group">
                <label className="mp-form-label">Teléfono</label>
                <input className="mp-form-input" type="tel" value={formData.phone_number} onChange={(e) => handleChange('phone_number', e.target.value)} placeholder="+56 9 1234 5678" disabled={isSubmitting} />
              </div>
              <div className="mp-form-group">
                <label className="mp-form-label">Fecha de Nacimiento</label>
                <button type="button" className="mp-form-input dob-button" onClick={() => setShowDobCalendar(!showDobCalendar)} disabled={isSubmitting}>
                  {showDobCalendar ? 'Ocultar' : formData.dob && parseDateString(formData.dob) ? formatDisplayDate(parseDateString(formData.dob)!) : 'Seleccionar fecha'}
                </button>
                {showDobCalendar && (
                  <div className="mp-calendar-container">
                    <Calendar onChange={handleDobChange} value={parseDateString(formData.dob) || new Date()} locale="es-ES" />
                  </div>
                )}
              </div>
            </div>

            <div className="mp-form-group">
              <label className="mp-form-label">Dirección</label>
              <input className="mp-form-input" type="text" value={formData.address} onChange={(e) => handleChange('address', e.target.value)} placeholder="Av. Principal 123" disabled={isSubmitting} />
            </div>
          </>
        )}

        {currentStep === 2 && (
          <>
            <div className="mp-form-group">
              <label className="mp-form-label">Rol *</label>
              <div className="mp-form-chips">
                {roles.map(role => (
                  <button key={role.rol_id} type="button" className={`mp-form-chip ${formData.rol_id === role.rol_id ? 'selected' : ''}`} onClick={() => handleChange('rol_id', role.rol_id)} disabled={isSubmitting}>
                    {role.rol_name}
                  </button>
                ))}
              </div>
            </div>

            <div className="mp-form-group">
              <label className="mp-form-label">Puesto de Trabajo *</label>
              {puestos.length === 0 ? (
                <p className="mp-form-error">No hay puestos de trabajo disponibles.</p>
              ) : (
                <div className="mp-form-chips">
                  {puestos.map(puesto => (
                    <button key={puesto.puesto_id} type="button" className={`mp-form-chip ${formData.puesto_id === puesto.puesto_id ? 'selected' : ''}`} onClick={() => handleChange('puesto_id', puesto.puesto_id)} disabled={isSubmitting}>
                      {puesto.puesto_name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mp-summary-card">
              <h4>Resumen</h4>
              <div className="mp-summary-item"><span className="label">Nombre</span><span className="value">{formData.username} {formData.last_name}</span></div>
              <div className="mp-summary-item"><span className="label">Email</span><span className="value">{formData.email}</span></div>
              {selectedRole && <div className="mp-summary-item"><span className="label">Rol</span><span className="value">{selectedRole.rol_name}</span></div>}
              {selectedPuesto && <div className="mp-summary-item"><span className="label">Puesto</span><span className="value">{selectedPuesto.puesto_name}</span></div>}
            </div>
          </>
        )}
      </div>

      <div className="mp-modal-footer">
        {currentStep === 1 ? (
          <>
            <button className="mp-modal-btn-cancel" onClick={handleClose} disabled={isSubmitting}>Cancelar</button>
            <button className="mp-modal-btn-submit" onClick={handleNextStep} disabled={isSubmitting}>
              Siguiente <HiOutlineChevronRight size={16} />
            </button>
          </>
        ) : (
          <>
            <button className="mp-modal-btn-cancel" onClick={handlePrevStep} disabled={isSubmitting}>
              <HiOutlineChevronLeft size={16} /> Atrás
            </button>
            <button className="mp-modal-btn-submit" onClick={handleSubmit} disabled={isSubmitting || !formData.rol_id || !formData.puesto_id}>
              {isSubmitting ? 'Creando...' : (<><HiOutlineUserPlus size={16} /> Crear Usuario</>)}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CreateUser;